import { createAction, handleActions } from 'redux-actions';
import { Api } from '../../api/api';
import {fetchSubmitForm, fetchSubmitFormFailed, fetchSubmitFormSuccess} from '../common/submitForm';
import { getUserId } from '../account/userData';
import { SUB_ROLES, fetchGetSubRoles } from '../account/role';
import { hideModal } from '../common/activeModal';
import { logout, onForbidden } from '../account/login';
import _ from 'lodash';

//- Actions
export const fetchGetDepartmentFailed = createAction('FETCH_GET_DEPARTMENT_FAILED');
export const fetchGetDepartmentSuccess = createAction('FETCH_GET_DEPARTMENT_SUCCESS');
export const fetchGetDepartment = createAction(
    'FETCH_GET_DEPARTMENT',
    id => Api.getDepartment(id),
    () => ({
        apiCall: true,
        onSuccess: fetchGetDepartmentSuccess,
        onFail: fetchGetDepartmentFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const fetchGetDepartmentBOsSuccess = createAction('FETCH_GET_DEPARTMENT_BOS_SUCCESS');
export const fetchGetDepartmentBOs = createAction(
    'FETCH_GET_DEPARTMENT_BOS',
    id => Api.getEmployeesByDepartmentAndRole(id, SUB_ROLES.BO),
    () => ({
        apiCall: true,
        onSuccess: fetchGetDepartmentBOsSuccess,
        onFail: fetchGetDepartmentFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const fetchGetDepartmentHMsSuccess = createAction('FETCH_GET_DEPARTMENT_HMS_SUCCESS');
export const fetchGetDepartmentHMs = createAction(
    'FETCH_GET_DEPARTMENT_HMS',
    id => Api.getEmployeesByDepartmentAndRole(id, SUB_ROLES.HM),
    () => ({
        apiCall: true,
        onSuccess: fetchGetDepartmentHMsSuccess,
        onFail: fetchGetDepartmentFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const fetchGetDepartmentData = () => (dispatch, getState) => {
    let departmentId = getDepartmentId(getState());
    if (!_.isNil(departmentId) && departmentId !== '') {
        dispatch(fetchGetDepartment(departmentId));
        dispatch(fetchGetDepartmentHMs(departmentId));
        dispatch(fetchGetDepartmentBOs(departmentId));
    } else {
        dispatch(fetchGetDepartmentFailed("ID_IS_NULL"))
    }
};

export const updateDepartmentEmployees = (employeeId) => (dispatch, getState) => {
    let departmentId = getDepartmentId(getState());
    dispatch(fetchGetDepartmentHMs(departmentId));
    dispatch(fetchGetDepartmentBOs(departmentId));
    dispatch(fetchSubmitFormSuccess());
    if (employeeId === getUserId(getState())) {
        dispatch(fetchGetSubRoles());
    }
};

export const onChangeEmployeesInDepartmentSuccess = (employeeId) => () => dispatch => {
    dispatch(hideModal());
    dispatch(updateDepartmentEmployees(employeeId));
    dispatch(fetchSubmitFormSuccess());
};

export const fetchAddEmployeeToDepartment = (departmentId, employeeId, role) => dispatch => {
    dispatch(fetchSubmitForm());
    dispatch(onAddEmployeeToDepartment(departmentId, employeeId, role));
};

export const onAddEmployeeToDepartment = createAction(
    'FETCH_ADD_EMPLOYEE_TO_DEPARTMENT',
    (departmentId, employeeId, role) => Api.addEmployeeToDepartment(departmentId, employeeId, role),
    (departmentId, employeeId) => ({
        apiCall: true,
        onSuccess: onChangeEmployeesInDepartmentSuccess(employeeId),
        onFail: fetchSubmitFormFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const fetchRemoveEmployeeFromDepartment = (departmentId, employeeId, role) => dispatch => {
    dispatch(fetchSubmitForm());
    dispatch(removeEmployeeFromDepartment(departmentId, employeeId, role));
};

export const removeEmployeeFromDepartment = createAction(
    'FETCH_REMOVE_EMPLOYEE_FROM_DEPARTMENT',
    (departmentId, employeeId, role) => Api.removeEmployeeFromDepartment(departmentId, employeeId, role),
    (departmentId, employeeId) => ({
        apiCall: true,
        onSuccess: onChangeEmployeesInDepartmentSuccess(employeeId),
        onFail: fetchSubmitFormFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const fetchEditDepartment = (departmentId, data) => dispatch => {
    dispatch(fetchSubmitForm());
    dispatch(onEditDepartment(departmentId, data));
};
export const onEditDepartmentSuccess = () => dispatch => {
    dispatch(hideModal());
    dispatch(fetchGetDepartmentData());
    dispatch(fetchSubmitFormSuccess())
};
export const onEditDepartment = createAction(
    'FETCH_EDIT_DEPARTMENT',
    (departmentId, data) => Api.editDepartment(departmentId, data),
    () => ({
        apiCall: true,
        onSuccess: onEditDepartmentSuccess,
        onFail: fetchSubmitFormFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const setDepartmentId = createAction('DEPARTMENT_ID_SET', id => id);
export const resetDepartmentData = createAction('DEPARTMENT_DATA_RESET');

//- State
const initialState = {
    error: null,
    fetching: false,
    success: false,
    departmentData: null,
    departmentHMsData: null,
    departmentBOsData: null,
    id: null
};

//- Reducers
export default handleActions({
    DEPARTMENT_ID_SET: (state, action) => {
        return {...state, id: action.payload};
    },
    FETCH_GET_DEPARTMENT: (state) => {
        return {...state, error: null, fetching: true, success: false};
    },
    FETCH_GET_DEPARTMENT_FAILED: (state, action) => {
        return {...state, error: action.payload, fetching: false, success: false};
    },
    FETCH_GET_DEPARTMENT_SUCCESS: (state, action) => {
        return {...state, error: null, fetching: false, success: true, departmentData: action.payload}
    },
    FETCH_GET_DEPARTMENT_HMS_SUCCESS: (state, action) => {
        return {...state, error: null, fetching: false, success: true, departmentHMsData: action.payload.content};
    },
    FETCH_GET_DEPARTMENT_BOS_SUCCESS: (state, action) => {
        return {...state, error: null, fetching: false, success: true, departmentBOsData: action.payload.content};
    },
    DEPARTMENT_DATA_RESET: (state) => {
        return {...state, departmentData: null, departmentHMsData: null, departmentBOsData: null}
    }
}, initialState);

//- Selectors
export const getDepartment = state => state.department;

export const getDepartmentData = state => getDepartment(state).departmentData;

export const getDepartmentHMsData = state => getDepartment(state).departmentHMsData;

export const getDepartmentBOsData = state => getDepartment(state).departmentBOsData;

export const loadingDepartment = state => {
    return getDepartment(state).fetching ||
        _.isNil(getDepartmentData(state)) ||
        _.isNil(getDepartmentHMsData(state)) ||
        _.isNil(getDepartmentBOsData(state)) ||
        _.isNil(getDepartmentData(state).name);
};

export const getFetchingErrorDepartment = state => getDepartment(state).error;

export const getDepartmentAndStaffData = state => {
    return {
        ...getDepartmentData(state),
        hms: getDepartmentHMsData(state),
        bos: getDepartmentBOsData(state)
    };
};

export const getDepartmentName = state => {
    if (_.isNil(getDepartmentData(state))) {
        return null;
    }

    return getDepartmentData(state).name;
};

export const getDepartmentEmail = state => {
    if (_.isNil(getDepartmentData(state))) {
        return null;
    }

    return getDepartmentData(state).email;
};

export const getDepartmentId = state => getDepartment(state).id;


// WEBPACK FOOTER //
// ./src/reducers/organization/department.js